import { CSSProperties, FC, useEffect, useRef, useState } from 'react'

// VESSEL INVADERS — TOS easter egg, Space Invaders inverted.
// You're a gantry crane on the terminal. An unauthorized convoy of cargo is
// rising from the harbor — vessels by sea, trains down the rail spurs,
// trucks up the terminal approach. Drop twistlocks onto them. Don't let any
// reach the terminal line.
//
// Controls:
//   ← →   move trolley along the rail
//   space drop twistlock (one in flight at a time)
//   P     pause
//   R     restart
//   M     mute
//   ESC   close
//
// CUSTOMIZE: search for `// CUSTOMIZE:` for tunable bits.

// ---- world ----
const WORLD_W = 760
const WORLD_H = 720
const RAIL_Y = 70
const TERMINAL_LINE_Y = 110

// ---- formation ----
const ENEMY_COLS = 10
const ENEMY_ROWS = 5
const ENEMY_W = 32
const ENEMY_H = 24
const ENEMY_SPACING_X = 16
const ENEMY_SPACING_Y = 16
const FORMATION_BOTTOM_START = WORLD_H - 110
const FORMATION_LEFT = 60
const FORMATION_STEP_X = 8
const FORMATION_STEP_Y = 22
const TICK_FRAMES_FULL = 36
const TICK_FRAMES_FEWEST = 4

// ---- crane (player) ----
const CRANE_SPEED = 4
const CRANE_W = 50
const CRANE_H = 26
const SPREADER_W = 24
const SPREADER_H = 6
const CABLE_LENGTH = 18

// ---- twistlocks (player projectile) ----
const TWISTLOCK_SPEED = 9
const VESSEL_BULLET_SPEED = 4
const VESSEL_BULLET_SPEED_FAST = 6
const MAX_VESSEL_BULLETS = 3
const VESSEL_FIRE_CHANCE_BASE = 0.014

// ---- bunkers (floating container barges) ----
const BUNKER_COUNT = 4
const BUNKER_GRID_W = 8
const BUNKER_GRID_H = 5
const BUNKER_CELL = 5
const BUNKER_Y = 200

// ---- speedboat (the "UFO" replacement) ----
const SPEEDBOAT_MIN_INTERVAL = 60 * 18
const SPEEDBOAT_MAX_INTERVAL = 60 * 35
const SPEEDBOAT_SPEED = 3.4
const SPEEDBOAT_SCORES = [100, 150, 200, 300]

// ---- power-up drops ----
const SHIELD_DROP_CHANCE = 0.05
const SHIELD_DURATION_FRAMES = 60 * 5
const SHIELD_RISE_SPEED = 1.8

// CUSTOMIZE: company name on game-over.
const COMPANY_NAME = 'TERMINAL OS'

type EnemyType = 'vessel' | 'train' | 'truck'

const ROW_TYPES: EnemyType[] = ['vessel', 'train', 'train', 'truck', 'truck']
const ROW_SCORES = [40, 20, 20, 10, 10]

function rand(min: number, max: number): number {
  return Math.random() * (max - min) + min
}
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

// ---- AUDIO ----
type WindowWithWebkitAudio = Window & {
  webkitAudioContext?: typeof AudioContext
}

class GameAudio {
  ctx: AudioContext | null = null
  masterGain: GainNode | null = null
  muted = false

  init() {
    if (this.ctx) return
    try {
      const Ctor = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext
      if (!Ctor) return
      this.ctx = new Ctor()
      this.masterGain = this.ctx.createGain()
      this.masterGain.gain.value = 0.22
      this.masterGain.connect(this.ctx.destination)
    } catch {
      // audio unavailable — fail silently
    }
  }
  private env(node: GainNode, attack: number, decay: number, peak = 1) {
    if (!this.ctx) return
    const t = this.ctx.currentTime
    node.gain.setValueAtTime(0, t)
    node.gain.linearRampToValueAtTime(peak, t + attack)
    node.gain.exponentialRampToValueAtTime(0.001, t + attack + decay)
  }
  step(beat: number) {
    if (!this.ctx || !this.masterGain || this.muted) return
    const tones = [98, 87, 78, 65]
    const freq = tones[beat % 4]
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'square'
    osc.frequency.value = freq
    this.env(gain, 0.005, 0.14, 0.22)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.15)
  }
  fire() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'square'
    osc.frequency.setValueAtTime(330, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(110, this.ctx.currentTime + 0.12)
    this.env(gain, 0.001, 0.13, 0.18)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 0.15)
  }
  hit(big: boolean) {
    if (!this.ctx || !this.masterGain || this.muted) return
    const dur = big ? 0.4 : 0.22
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const filter = this.ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = big ? 500 : 1000
    const gain = this.ctx.createGain()
    this.env(gain, 0.005, dur, big ? 0.55 : 0.32)
    src.connect(filter).connect(gain).connect(this.masterGain)
    src.start()
    src.stop(this.ctx.currentTime + dur)

    if (big) {
      const osc = this.ctx.createOscillator()
      const g2 = this.ctx.createGain()
      osc.type = 'triangle'
      osc.frequency.setValueAtTime(880, this.ctx.currentTime)
      osc.frequency.exponentialRampToValueAtTime(220, this.ctx.currentTime + 0.2)
      this.env(g2, 0.001, 0.2, 0.2)
      osc.connect(g2).connect(this.masterGain)
      osc.start()
      osc.stop(this.ctx.currentTime + 0.22)
    }
  }
  speedboat() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(180, this.ctx.currentTime)
    osc.frequency.linearRampToValueAtTime(280, this.ctx.currentTime + 0.6)
    osc.frequency.linearRampToValueAtTime(120, this.ctx.currentTime + 1.2)
    this.env(gain, 0.05, 1.2, 0.18)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + 1.25)
  }
  death() {
    if (!this.ctx || !this.masterGain || this.muted) return
    const dur = 0.8
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(440, this.ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(60, this.ctx.currentTime + dur)
    this.env(gain, 0.005, dur, 0.35)
    osc.connect(gain).connect(this.masterGain)
    osc.start()
    osc.stop(this.ctx.currentTime + dur + 0.05)

    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    const f = this.ctx.createBiquadFilter()
    f.type = 'lowpass'
    f.frequency.value = 700
    const g2 = this.ctx.createGain()
    this.env(g2, 0.005, dur, 0.4)
    src.connect(f).connect(g2).connect(this.masterGain)
    src.start()
    src.stop(this.ctx.currentTime + dur + 0.05)
  }
  shieldGet() {
    if (!this.ctx || !this.masterGain || this.muted) return
    ;[880, 1320, 1760, 2200].forEach((freq, i) => {
      if (!this.ctx || !this.masterGain) return
      const osc = this.ctx.createOscillator()
      const gain = this.ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      const t = this.ctx.currentTime + i * 0.04
      gain.gain.setValueAtTime(0, t)
      gain.gain.linearRampToValueAtTime(0.18, t + 0.005)
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.12)
      osc.connect(gain).connect(this.masterGain)
      osc.start(t)
      osc.stop(t + 0.14)
    })
  }
  toggleMute() {
    this.muted = !this.muted
  }
}

// ---- entity types ----
interface Enemy {
  col: number
  row: number
  x: number
  y: number
  type: EnemyType
  score: number
  alive: boolean
  animFrame: number
  bobPhase: number
}

interface Bunker {
  x: number
  y: number
  grid: number[][]
}

interface Crane {
  x: number
  y: number
  cooldown: number
  explodeFrames: number
  shieldFrames: number
}

interface Twistlock {
  x: number
  y: number
  rotation: number
}

interface EnemyBullet {
  x: number
  y: number
  fast: boolean
}

interface Speedboat {
  x: number
  y: number
  dir: number
  score: number
  spray: number
}

interface ShieldDrop {
  x: number
  y: number
}

interface Floater {
  x: number
  y: number
  text: string
  color: string
  life: number
}

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  color: string
  size: number
}

interface ExplosionFx {
  x: number
  y: number
  frames: number
}

type GameStatus = 'ready' | 'playing' | 'paused' | 'gameover'

interface GameState {
  enemies: Enemy[]
  formationDir: number
  formationStepCounter: number
  formationBeat: number
  formationAnimToggle: number
  bunkers: Bunker[]
  crane: Crane
  twistlock: Twistlock | null
  enemyBullets: EnemyBullet[]
  speedboat: Speedboat | null
  speedboatNextSpawn: number
  shieldDrops: ShieldDrop[]
  score: number
  hi: number
  lives: number
  level: number
  status: GameStatus
  time: number
  shake: number
  floaters: Floater[]
  particles: Particle[]
  explosions: ExplosionFx[]
}

interface HudState {
  score: number
  hi: number
  lives: number
  level: number
  status: GameStatus
  muted: boolean
}

// ---- entity factories ----
function makeFormation(level: number): Enemy[] {
  const enemies: Enemy[] = []
  const startY =
    FORMATION_BOTTOM_START - (ENEMY_ROWS - 1) * (ENEMY_H + ENEMY_SPACING_Y) - (level - 1) * 18
  for (let row = 0; row < ENEMY_ROWS; row++) {
    for (let col = 0; col < ENEMY_COLS; col++) {
      enemies.push({
        col,
        row,
        x: FORMATION_LEFT + col * (ENEMY_W + ENEMY_SPACING_X) + ENEMY_W / 2,
        y: startY + row * (ENEMY_H + ENEMY_SPACING_Y) + ENEMY_H / 2,
        type: ROW_TYPES[row],
        score: ROW_SCORES[row],
        alive: true,
        animFrame: 0,
        bobPhase: rand(0, Math.PI * 2),
      })
    }
  }
  return enemies
}

function makeBunkers(): Bunker[] {
  const totalW = WORLD_W - 100
  const slotW = totalW / BUNKER_COUNT
  const bunkerW = BUNKER_GRID_W * BUNKER_CELL
  const bunkers: Bunker[] = []
  for (let i = 0; i < BUNKER_COUNT; i++) {
    const cx = 50 + slotW * (i + 0.5)
    const x = cx - bunkerW / 2
    const grid: number[][] = []
    for (let r = 0; r < BUNKER_GRID_H; r++) {
      const rowArr: number[] = []
      for (let c = 0; c < BUNKER_GRID_W; c++) {
        const inNotch = r <= 1 && c >= 2 && c <= BUNKER_GRID_W - 3
        rowArr.push(inNotch ? 0 : 1)
      }
      grid.push(rowArr)
    }
    bunkers.push({ x, y: BUNKER_Y, grid })
  }
  return bunkers
}

function makeCrane(): Crane {
  return {
    x: WORLD_W / 2,
    y: RAIL_Y,
    cooldown: 0,
    explodeFrames: 0,
    shieldFrames: 0,
  }
}

interface VesselInvadersProps {
  onClose?: () => void
}

const VesselInvaders: FC<VesselInvadersProps> = ({ onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const stateRef = useRef<GameState | null>(null)
  const keysRef = useRef<Record<string, boolean>>({})
  const audioRef = useRef<GameAudio>(new GameAudio())
  const startGameRef = useRef<(() => void) | null>(null)
  const [hud, setHud] = useState<HudState>({
    score: 0,
    hi: 0,
    lives: 3,
    level: 1,
    status: 'ready',
    muted: false,
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const audio = audioRef.current

    const dpr = window.devicePixelRatio || 1
    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    let savedHi = 0
    try {
      savedHi = parseInt(localStorage.getItem('vesselInvadersHi') || '0', 10) || 0
    } catch {
      // localStorage unavailable
    }

    const initState = (): GameState => ({
      enemies: makeFormation(1),
      formationDir: 1,
      formationStepCounter: 0,
      formationBeat: 0,
      formationAnimToggle: 0,
      bunkers: makeBunkers(),
      crane: makeCrane(),
      twistlock: null,
      enemyBullets: [],
      speedboat: null,
      speedboatNextSpawn:
        SPEEDBOAT_MIN_INTERVAL +
        Math.floor(rand(0, SPEEDBOAT_MAX_INTERVAL - SPEEDBOAT_MIN_INTERVAL)),
      shieldDrops: [],
      score: 0,
      hi: savedHi,
      lives: 3,
      level: 1,
      status: 'ready',
      time: 0,
      shake: 0,
      floaters: [],
      particles: [],
      explosions: [],
    })

    stateRef.current = initState()

    const startGame = () => {
      const s = initState()
      s.status = 'playing'
      stateRef.current = s
      setHud({
        score: 0,
        hi: s.hi,
        lives: 3,
        level: 1,
        status: 'playing',
        muted: audio.muted,
      })
    }
    startGameRef.current = startGame

    const onKeyDown = (e: KeyboardEvent) => {
      audio.init()
      keysRef.current[e.key] = true
      if (['ArrowLeft', 'ArrowRight', ' '].includes(e.key)) e.preventDefault()
      if (e.key === 'Escape' && onClose) onClose()
      const s = stateRef.current
      if (!s) return
      if (s.status === 'ready' && (e.key === ' ' || /^Arrow/.test(e.key))) {
        startGame()
        return
      }
      if (e.key === 'p' || e.key === 'P') {
        if (s.status === 'playing') s.status = 'paused'
        else if (s.status === 'paused') s.status = 'playing'
        setHud(p => ({ ...p, status: s.status }))
      }
      if (e.key === 'm' || e.key === 'M') {
        audio.toggleMute()
        setHud(p => ({ ...p, muted: audio.muted }))
      }
      if (e.key === 'r' || e.key === 'R') {
        const cur = s.status
        if (cur === 'gameover' || cur === 'ready') startGame()
        else if (cur === 'playing' || cur === 'paused') {
          if (window.confirm('Restart current shift? Your progress will be lost.')) startGame()
        }
      }
    }
    const onKeyUp = (e: KeyboardEvent) => {
      keysRef.current[e.key] = false
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)

    const explode = (x: number, y: number, color: string, count = 14) => {
      const s = stateRef.current
      if (!s) return
      for (let i = 0; i < count; i++) {
        const a = rand(0, Math.PI * 2)
        const speed = rand(1, 4)
        s.particles.push({
          x,
          y,
          vx: Math.cos(a) * speed,
          vy: Math.sin(a) * speed,
          life: rand(20, 40),
          color,
          size: rand(1.5, 3),
        })
      }
    }

    const showFloater = (x: number, y: number, text: string, color = '#ffd166') => {
      const s = stateRef.current
      if (!s) return
      s.floaters.push({ x, y, text, color, life: 60 })
    }

    const currentTickFrames = (): number => {
      const s = stateRef.current
      if (!s) return TICK_FRAMES_FULL
      const aliveCount = s.enemies.filter(e => e.alive).length
      const totalCount = ENEMY_COLS * ENEMY_ROWS
      const norm = clamp(aliveCount / totalCount, 0, 1)
      const curved = Math.pow(norm, 1.6)
      return Math.round(TICK_FRAMES_FEWEST + (TICK_FRAMES_FULL - TICK_FRAMES_FEWEST) * curved)
    }

    const formationShouldReverse = (): boolean => {
      const s = stateRef.current
      if (!s) return false
      const alive = s.enemies.filter(e => e.alive)
      if (alive.length === 0) return false
      let minX = Infinity,
        maxX = -Infinity
      for (const e of alive) {
        const halfW = ENEMY_W / 2
        if (e.x - halfW < minX) minX = e.x - halfW
        if (e.x + halfW > maxX) maxX = e.x + halfW
      }
      const margin = 30
      if (s.formationDir > 0 && maxX + FORMATION_STEP_X > WORLD_W - margin) return true
      if (s.formationDir < 0 && minX - FORMATION_STEP_X < margin) return true
      return false
    }

    const stepFormation = () => {
      const s = stateRef.current
      if (!s) return
      const reverse = formationShouldReverse()
      if (reverse) {
        s.formationDir *= -1
        for (const e of s.enemies) {
          if (e.alive) e.y -= FORMATION_STEP_Y
        }
      } else {
        for (const e of s.enemies) {
          if (e.alive) e.x += FORMATION_STEP_X * s.formationDir
        }
      }
      s.formationAnimToggle = 1 - s.formationAnimToggle
      for (const e of s.enemies) e.animFrame = s.formationAnimToggle
      audio.step(s.formationBeat)
      s.formationBeat++
    }

    const tryEnemyFire = () => {
      const s = stateRef.current
      if (!s) return
      if (s.enemyBullets.length >= MAX_VESSEL_BULLETS) return
      const aliveCount = s.enemies.filter(e => e.alive).length
      const totalCount = ENEMY_COLS * ENEMY_ROWS
      const fireRate =
        VESSEL_FIRE_CHANCE_BASE * (aliveCount / totalCount + 0.4) * (1 + (s.level - 1) * 0.2)
      if (Math.random() > fireRate) return
      const frontLine: Enemy[] = []
      for (let c = 0; c < ENEMY_COLS; c++) {
        let top: Enemy | null = null
        for (const e of s.enemies) {
          if (e.col === c && e.alive) {
            if (!top || e.y < top.y) top = e
          }
        }
        if (top) frontLine.push(top)
      }
      if (frontLine.length === 0) return
      const shooter = frontLine[Math.floor(Math.random() * frontLine.length)]
      const fast = Math.random() < 0.25 + (s.level - 1) * 0.05
      s.enemyBullets.push({
        x: shooter.x,
        y: shooter.y - ENEMY_H / 2,
        fast,
      })
    }

    interface Projectile {
      x: number
      y: number
    }

    const bunkerCollide = (
      bullet: Projectile,
      bullets: Projectile[],
      fromBelow: boolean,
    ): boolean => {
      const s = stateRef.current
      if (!s) return false
      for (const b of s.bunkers) {
        const bw = BUNKER_GRID_W * BUNKER_CELL
        const bh = BUNKER_GRID_H * BUNKER_CELL
        if (bullet.x < b.x || bullet.x > b.x + bw || bullet.y < b.y || bullet.y > b.y + bh) continue
        const cx = Math.floor((bullet.x - b.x) / BUNKER_CELL)
        const cy = Math.floor((bullet.y - b.y) / BUNKER_CELL)
        if (cy < 0 || cy >= BUNKER_GRID_H || cx < 0 || cx >= BUNKER_GRID_W) continue
        if (b.grid[cy][cx]) {
          b.grid[cy][cx] = 0
          const neighbor = fromBelow ? cy + 1 : cy - 1
          if (neighbor >= 0 && neighbor < BUNKER_GRID_H && b.grid[neighbor][cx]) {
            if (Math.random() < 0.5) b.grid[neighbor][cx] = 0
          }
          explode(bullet.x, bullet.y, '#a85e22', 6)
          const idx = bullets.indexOf(bullet)
          if (idx >= 0) bullets.splice(idx, 1)
          else if (s.twistlock && (s.twistlock as Projectile) === bullet) s.twistlock = null
          audio.hit(false)
          return true
        }
      }
      return false
    }

    let animationId = 0

    const draw = () => {
      const s = stateRef.current
      if (!s) {
        animationId = requestAnimationFrame(draw)
        return
      }
      s.time++

      const screenW = canvas.width / dpr
      const screenH = canvas.height / dpr
      const scale = Math.min(screenW / WORLD_W, screenH / WORLD_H, 1.8)
      const offsetX = (screenW - WORLD_W * scale) / 2
      const offsetY = (screenH - WORLD_H * scale) / 2

      let shakeX = 0,
        shakeY = 0
      if (s.shake > 0) {
        shakeX = rand(-s.shake, s.shake)
        shakeY = rand(-s.shake, s.shake)
        s.shake *= 0.85
        if (s.shake < 0.3) s.shake = 0
      }
      ctx.setTransform(dpr, 0, 0, dpr, shakeX * dpr, shakeY * dpr)

      // ---- background ----
      const sky = ctx.createLinearGradient(0, 0, 0, screenH)
      sky.addColorStop(0, '#0d1622')
      sky.addColorStop(0.45, '#1a2436')
      sky.addColorStop(0.55, '#0f1a28')
      sky.addColorStop(1, '#060c14')
      ctx.fillStyle = sky
      ctx.fillRect(0, 0, screenW, screenH)

      ctx.save()
      ctx.translate(offsetX, offsetY)
      ctx.scale(scale, scale)

      for (let i = 0; i < 40; i++) {
        const x = (i * 137) % WORLD_W
        const y = (i * 89) % (WORLD_H * 0.4)
        const alpha = 0.2 + Math.sin((s.time + i * 11) * 0.05) * 0.15
        ctx.fillStyle = `rgba(180, 200, 230, ${alpha})`
        ctx.fillRect(x, y, 1, 1)
      }

      ctx.strokeStyle = 'rgba(80, 140, 180, 0.1)'
      ctx.lineWidth = 1
      for (let i = 0; i < 8; i++) {
        const y = WORLD_H * 0.5 + i * 25
        ctx.beginPath()
        ctx.moveTo(0, y)
        for (let x = 0; x <= WORLD_W; x += 30) {
          ctx.lineTo(x, y + Math.sin((x + s.time * 1.2) * 0.025) * 2.5)
        }
        ctx.stroke()
      }

      // ---- the TERMINAL at the top ----
      const terminalGrad = ctx.createLinearGradient(0, 0, 0, TERMINAL_LINE_Y)
      terminalGrad.addColorStop(0, '#3a4458')
      terminalGrad.addColorStop(1, '#2a3445')
      ctx.fillStyle = terminalGrad
      ctx.fillRect(0, 0, WORLD_W, TERMINAL_LINE_Y)
      ctx.fillStyle = '#c9a648'
      ctx.fillRect(0, TERMINAL_LINE_Y - 4, WORLD_W, 2)
      ctx.fillStyle = '#1a1a1a'
      for (let x = 0; x < WORLD_W; x += 16) {
        ctx.beginPath()
        ctx.moveTo(x, TERMINAL_LINE_Y - 4)
        ctx.lineTo(x + 4, TERMINAL_LINE_Y - 4)
        ctx.lineTo(x, TERMINAL_LINE_Y - 2)
        ctx.lineTo(x - 4, TERMINAL_LINE_Y - 2)
        ctx.closePath()
        ctx.fill()
      }
      ctx.strokeStyle = 'rgba(217, 74, 74, 0.7)'
      ctx.lineWidth = 1.5
      ctx.setLineDash([6, 6])
      ctx.beginPath()
      ctx.moveTo(0, TERMINAL_LINE_Y)
      ctx.lineTo(WORLD_W, TERMINAL_LINE_Y)
      ctx.stroke()
      ctx.setLineDash([])
      ctx.fillStyle = 'rgba(217, 74, 74, 0.6)'
      ctx.font = '9px ui-monospace, Menlo, monospace'
      ctx.textAlign = 'center'
      ctx.fillText('· TERMINAL · DO NOT BREACH ·', WORLD_W / 2, TERMINAL_LINE_Y + 12)

      // ---- the GANTRY CRANE structure ----
      drawCraneStructure(ctx)

      // ---- update game ----
      if (s.status === 'playing' && s.crane.explodeFrames === 0) {
        if (keysRef.current['ArrowLeft']) s.crane.x -= CRANE_SPEED
        if (keysRef.current['ArrowRight']) s.crane.x += CRANE_SPEED
        s.crane.x = clamp(s.crane.x, 50, WORLD_W - 50)

        if (keysRef.current[' '] && !s.twistlock && s.crane.cooldown === 0) {
          s.twistlock = {
            x: s.crane.x,
            y: s.crane.y + CRANE_H + CABLE_LENGTH,
            rotation: 0,
          }
          s.crane.cooldown = 6
          audio.fire()
        }
        if (s.crane.cooldown > 0) s.crane.cooldown--
        if (s.crane.shieldFrames > 0) s.crane.shieldFrames--

        if (s.twistlock) {
          s.twistlock.y += TWISTLOCK_SPEED
          s.twistlock.rotation += 0.4
          if (s.twistlock.y > WORLD_H) s.twistlock = null
          else bunkerCollide(s.twistlock, [], false)
        }

        s.enemyBullets = s.enemyBullets.filter(b => {
          b.y -= b.fast ? VESSEL_BULLET_SPEED_FAST : VESSEL_BULLET_SPEED
          if (b.y < 0) return false
          if (bunkerCollide(b, s.enemyBullets, true)) return false
          return true
        })

        s.formationStepCounter--
        if (s.formationStepCounter <= 0) {
          stepFormation()
          s.formationStepCounter = currentTickFrames()
        }

        for (const e of s.enemies) {
          if (e.alive) e.bobPhase += 0.05
        }

        tryEnemyFire()

        if (s.speedboat) {
          s.speedboat.x += SPEEDBOAT_SPEED * s.speedboat.dir
          s.speedboat.spray += 0.3
          if (
            (s.speedboat.dir > 0 && s.speedboat.x > WORLD_W + 40) ||
            (s.speedboat.dir < 0 && s.speedboat.x < -40)
          ) {
            s.speedboat = null
          }
        } else {
          s.speedboatNextSpawn--
          if (s.speedboatNextSpawn <= 0) {
            const dir = Math.random() > 0.5 ? 1 : -1
            s.speedboat = {
              x: dir > 0 ? -30 : WORLD_W + 30,
              y: WORLD_H - 45,
              dir,
              score: SPEEDBOAT_SCORES[Math.floor(Math.random() * SPEEDBOAT_SCORES.length)],
              spray: 0,
            }
            audio.speedboat()
            s.speedboatNextSpawn =
              SPEEDBOAT_MIN_INTERVAL +
              Math.floor(rand(0, SPEEDBOAT_MAX_INTERVAL - SPEEDBOAT_MIN_INTERVAL))
          }
        }

        if (s.twistlock) {
          for (const e of s.enemies) {
            if (!e.alive) continue
            if (
              Math.abs(s.twistlock.x - e.x) < ENEMY_W / 2 &&
              Math.abs(s.twistlock.y - e.y) < ENEMY_H / 2
            ) {
              e.alive = false
              s.score += e.score
              s.explosions.push({ x: e.x, y: e.y, frames: 18 })
              const color =
                e.type === 'truck' ? '#3a8fd6' : e.type === 'train' ? '#d97a2c' : '#d94a4a'
              explode(e.x, e.y, color, 12)
              audio.hit(e.type === 'vessel')
              showFloater(e.x, e.y - 14, `+${e.score}`, '#ffd166')
              s.twistlock = null
              if (Math.random() < SHIELD_DROP_CHANCE) {
                s.shieldDrops.push({ x: e.x, y: e.y })
              }
              break
            }
          }
        }

        if (s.twistlock && s.speedboat) {
          if (
            Math.abs(s.twistlock.x - s.speedboat.x) < 24 &&
            Math.abs(s.twistlock.y - s.speedboat.y) < 12
          ) {
            s.score += s.speedboat.score
            showFloater(s.speedboat.x, s.speedboat.y - 14, `+${s.speedboat.score}`, '#d94a8f')
            audio.hit(true)
            explode(s.speedboat.x, s.speedboat.y, '#d94a8f', 18)
            s.speedboat = null
            s.twistlock = null
          }
        }

        s.shieldDrops = s.shieldDrops.filter(d => {
          d.y -= SHIELD_RISE_SPEED
          if (Math.abs(d.x - s.crane.x) < 28 && Math.abs(d.y - s.crane.y) < 18) {
            s.crane.shieldFrames = SHIELD_DURATION_FRAMES
            audio.shieldGet()
            showFloater(s.crane.x, s.crane.y + 24, 'SHIELD ACTIVE', '#3a9bd6')
            return false
          }
          if (d.y < 0) return false
          return true
        })

        for (const b of s.enemyBullets) {
          if (
            Math.abs(b.x - s.crane.x) < CRANE_W / 2 &&
            b.y < s.crane.y + CRANE_H &&
            b.y > s.crane.y - 10
          ) {
            if (s.crane.shieldFrames > 0) {
              s.shake = 4
              explode(s.crane.x, s.crane.y + 8, '#3a9bd6', 8)
              const idx = s.enemyBullets.indexOf(b)
              if (idx >= 0) s.enemyBullets.splice(idx, 1)
            } else {
              s.crane.explodeFrames = 60
              s.shake = 14
              audio.death()
              explode(s.crane.x, s.crane.y, '#ffd166', 24)
              s.lives--
              s.enemyBullets = []
              s.twistlock = null
              break
            }
          }
        }

        for (const e of s.enemies) {
          if (e.alive && e.y - ENEMY_H / 2 <= TERMINAL_LINE_Y) {
            s.status = 'gameover'
            s.shake = 20
            audio.death()
            if (s.score > s.hi) {
              s.hi = s.score
              try {
                localStorage.setItem('vesselInvadersHi', String(s.hi))
              } catch {
                // localStorage unavailable
              }
            }
            setHud(p => ({ ...p, status: 'gameover', hi: s.hi }))
            break
          }
        }

        if (s.enemies.every(e => !e.alive)) {
          s.level++
          s.enemies = makeFormation(s.level)
          s.formationDir = 1
          s.formationBeat = 0
          showFloater(WORLD_W / 2, WORLD_H / 2, `WAVE ${s.level}`, '#ffd166')
        }

        s.explosions = s.explosions.filter(e => {
          e.frames--
          return e.frames > 0
        })
        s.particles = s.particles.filter(p => {
          p.x += p.vx
          p.y += p.vy
          p.vy += 0.15
          p.life--
          return p.life > 0
        })
        s.floaters = s.floaters.filter(f => {
          f.y -= 0.5
          f.life--
          return f.life > 0
        })

        setHud(prev => {
          if (
            prev.score === s.score &&
            prev.lives === s.lives &&
            prev.level === s.level &&
            prev.status === s.status
          )
            return prev
          return { ...prev, score: s.score, lives: s.lives, level: s.level, status: s.status }
        })
      } else if (s.crane.explodeFrames > 0) {
        s.crane.explodeFrames--
        if (s.crane.explodeFrames === 0) {
          if (s.lives < 0) {
            s.status = 'gameover'
            if (s.score > s.hi) {
              s.hi = s.score
              try {
                localStorage.setItem('vesselInvadersHi', String(s.hi))
              } catch {
                // localStorage unavailable
              }
            }
            setHud(p => ({ ...p, status: 'gameover', hi: s.hi }))
          } else {
            s.crane = makeCrane()
          }
        }
      }

      // ---- DRAW ----
      drawBunkers(ctx, s.bunkers)
      ctx.strokeStyle = 'rgba(80, 140, 180, 0.15)'
      ctx.lineWidth = 0.8
      for (const b of s.bunkers) {
        const bw = BUNKER_GRID_W * BUNKER_CELL
        const by = b.y + BUNKER_GRID_H * BUNKER_CELL + 2
        ctx.beginPath()
        ctx.moveTo(b.x - 4, by)
        for (let x = b.x - 4; x <= b.x + bw + 4; x += 4) {
          ctx.lineTo(x, by + Math.sin((x + s.time * 1.5) * 0.1) * 1.5)
        }
        ctx.stroke()
      }

      for (const e of s.enemies) {
        if (!e.alive) continue
        drawVessel(ctx, e)
      }

      for (const ex of s.explosions) drawExplosion(ctx, ex.x, ex.y, ex.frames)

      if (s.twistlock) drawTwistlock(ctx, s.twistlock)

      for (const b of s.enemyBullets) drawVesselBullet(ctx, b)

      if (s.speedboat) drawSpeedboat(ctx, s.speedboat, s.time)

      for (const d of s.shieldDrops) drawShieldDrop(ctx, d, s.time)

      drawCrane(ctx, s.crane, s.time)

      s.particles.forEach(p => {
        const alpha = Math.min(1, p.life / 25)
        ctx.fillStyle = p.color
        ctx.globalAlpha = alpha
        ctx.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size)
      })
      ctx.globalAlpha = 1

      s.floaters.forEach(f => {
        const alpha = Math.min(1, f.life / 30)
        ctx.fillStyle = f.color
        ctx.globalAlpha = alpha
        ctx.font = 'bold 12px ui-monospace, Menlo, monospace'
        ctx.textAlign = 'center'
        ctx.fillText(f.text, f.x, f.y)
      })
      ctx.globalAlpha = 1

      ctx.restore()

      const vignette = ctx.createRadialGradient(
        screenW / 2,
        screenH / 2,
        screenH * 0.4,
        screenW / 2,
        screenH / 2,
        screenH,
      )
      vignette.addColorStop(0, 'rgba(0,0,0,0)')
      vignette.addColorStop(1, 'rgba(0,0,0,0.5)')
      ctx.fillStyle = vignette
      ctx.fillRect(0, 0, screenW, screenH)

      animationId = requestAnimationFrame(draw)
    }
    animationId = requestAnimationFrame(draw)

    return () => {
      cancelAnimationFrame(animationId)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('resize', resize)
    }
  }, [onClose])

  return (
    <div style={styles.root}>
      <div style={styles.hud}>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>SCORE</span>
          <span style={styles.hudValue}>{String(hud.score).padStart(6, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>BEST</span>
          <span style={styles.hudValue}>{String(hud.hi).padStart(6, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>WAVE</span>
          <span style={styles.hudValue}>{String(hud.level).padStart(2, '0')}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>CRANES</span>
          <span style={styles.hudValue}>{'▣ '.repeat(Math.max(0, hud.lives))}</span>
        </div>
        <div style={styles.hudGroup}>
          <span style={styles.hudLabel}>AUDIO</span>
          <span style={styles.hudValue}>{hud.muted ? '○ MUTE' : '● ON'}</span>
        </div>
        <button
          style={styles.restart}
          onClick={() => {
            if (hud.status === 'playing' || hud.status === 'paused') {
              if (!window.confirm('Restart current shift? Your progress will be lost.')) return
            }
            audioRef.current.init()
            startGameRef.current?.()
          }}
          onMouseDown={e => e.preventDefault()}
          title='Restart shift (R)'
        >
          ↻ RESTART
        </button>
        {onClose && (
          <button style={styles.close} onClick={onClose}>
            ESC
          </button>
        )}
      </div>

      <canvas ref={canvasRef} style={styles.canvas} />

      {hud.status === 'ready' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>VESSEL INVADERS</div>
          <div style={styles.overlaySubLarge}>
            unauthorized cargo inbound by sea, rail, and road · stop them at the terminal
          </div>
          <div style={styles.legend}>
            <div style={styles.legendRow}>
              <span style={{ color: '#d94a4a' }}>▲</span> VESSELS — closest to terminal, highest
              threat (40 pts)
            </div>
            <div style={styles.legendRow}>
              <span style={{ color: '#d97a2c' }}>▲</span> TRAINS — flatcar convoy (20 pts)
            </div>
            <div style={styles.legendRow}>
              <span style={{ color: '#3a8fd6' }}>▲</span> TRUCKS — fast skirmishers (10 pts)
            </div>
            <div style={styles.legendRow}>
              <span style={{ color: '#d94a8f' }}>▲</span> SPEEDBOAT — bonus, races across (100-300
              pts)
            </div>
          </div>
          <div style={styles.controls}>
            <span style={{ color: '#cfd8e8' }}>
              ← → traverse the rail &nbsp;·&nbsp; SPACE drop twistlock &nbsp;·&nbsp; P pause
              &nbsp;·&nbsp; M mute
            </span>
            <br />
            <span style={{ color: '#5a6478' }}>
              shelter behind container barges · they erode under fire
            </span>
          </div>
          <div style={{ ...styles.controls, marginTop: 18 }}>press SPACE to start</div>
        </div>
      )}

      {hud.status === 'paused' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>BERTH HOLD</div>
          <div style={styles.overlaySub}>press P to resume</div>
        </div>
      )}

      {hud.status === 'gameover' && (
        <div style={styles.overlay}>
          <div style={styles.overlayTitle}>TERMINAL BREACHED</div>
          <div style={styles.overlaySub}>
            final score: {hud.score.toLocaleString()} · cleared {hud.level - 1} wave
            {hud.level - 1 !== 1 ? 's' : ''}
            {hud.score >= hud.hi && hud.score > 0 ? ' · NEW BEST' : ''}
          </div>
          <pre style={styles.report}>
            {`INCIDENT REPORT — ${COMPANY_NAME}
unauthorized vessel reached the terminal
${hud.level - 1} wave${hud.level - 1 !== 1 ? 's' : ''} successfully repelled
${hud.score >= hud.hi && hud.score > 0 ? 'operator commendation issued' : 'see you next shift'}`}
          </pre>
          <div style={styles.overlaySub}>press R or click RESTART to try again · ESC to close</div>
        </div>
      )}
    </div>
  )
}

export default VesselInvaders

// ============= DRAWING =============

function drawCraneStructure(ctx: CanvasRenderingContext2D) {
  const railY = RAIL_Y - 8
  const beamGrad = ctx.createLinearGradient(0, railY, 0, railY + 16)
  beamGrad.addColorStop(0, '#5a4520')
  beamGrad.addColorStop(0.5, '#c9a648')
  beamGrad.addColorStop(1, '#5a4520')
  ctx.fillStyle = beamGrad
  ctx.fillRect(20, railY, WORLD_W - 40, 12)
  ctx.strokeStyle = 'rgba(0,0,0,0.4)'
  ctx.lineWidth = 0.8
  for (let x = 26; x < WORLD_W - 30; x += 18) {
    ctx.beginPath()
    ctx.moveTo(x, railY)
    ctx.lineTo(x + 9, railY + 10)
    ctx.moveTo(x + 9, railY)
    ctx.lineTo(x, railY + 10)
    ctx.stroke()
  }
  ctx.fillStyle = '#3a2c12'
  ctx.fillRect(18, railY - 1, 2, 14)
  ctx.fillRect(WORLD_W - 20, railY - 1, 2, 14)

  ctx.strokeStyle = '#3a3a3a'
  ctx.lineWidth = 4
  ctx.beginPath()
  ctx.moveTo(20, railY + 12)
  ctx.lineTo(8, WORLD_H)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(20, railY + 12)
  ctx.lineTo(45, WORLD_H)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(WORLD_W - 20, railY + 12)
  ctx.lineTo(WORLD_W - 8, WORLD_H)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(WORLD_W - 20, railY + 12)
  ctx.lineTo(WORLD_W - 45, WORLD_H)
  ctx.stroke()
  ctx.strokeStyle = 'rgba(80, 80, 80, 0.7)'
  ctx.lineWidth = 1.5
  for (let i = 0; i < 6; i++) {
    const yL = railY + 70 + i * 90
    if (yL > WORLD_H) break
    const leftXa = 20 + ((yL - railY - 12) * (8 - 20)) / (WORLD_H - railY - 12)
    const leftXb = 20 + ((yL - railY - 12) * (45 - 20)) / (WORLD_H - railY - 12)
    ctx.beginPath()
    ctx.moveTo(leftXa, yL)
    ctx.lineTo(leftXb, yL + 30)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(leftXb, yL)
    ctx.lineTo(leftXa, yL + 30)
    ctx.stroke()
    const rightXa = WORLD_W - 20 + ((yL - railY - 12) * (-8 - -20)) / (WORLD_H - railY - 12)
    const rightXb = WORLD_W - 20 + ((yL - railY - 12) * (-45 - -20)) / (WORLD_H - railY - 12)
    ctx.beginPath()
    ctx.moveTo(rightXa, yL)
    ctx.lineTo(rightXb, yL + 30)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(rightXb, yL)
    ctx.lineTo(rightXa, yL + 30)
    ctx.stroke()
  }
}

function drawCrane(ctx: CanvasRenderingContext2D, c: Crane, time: number) {
  if (c.explodeFrames > 0) {
    const t = 1 - c.explodeFrames / 60
    const palette = ['#ffd166', '#d97a2c', '#cfd8e8', '#1a2236']
    for (let i = 0; i < 18; i++) {
      ctx.fillStyle = palette[i % palette.length]
      const ox = Math.sin(i * 1.3 + time * 0.5) * 24 * t
      const oy = Math.cos(i * 0.9 + time * 0.3) * 16 * t
      ctx.globalAlpha = 1 - t
      ctx.fillRect(c.x + ox, c.y + oy + 14, 4, 4)
    }
    ctx.globalAlpha = 1
    return
  }

  const cx = c.x,
    cy = c.y

  ctx.fillStyle = '#0d0d0d'
  ctx.beginPath()
  ctx.arc(cx - 16, cy - 4, 4, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.arc(cx + 16, cy - 4, 4, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#5a5a5a'
  ctx.beginPath()
  ctx.arc(cx - 16, cy - 4, 2, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.arc(cx + 16, cy - 4, 2, 0, Math.PI * 2)
  ctx.fill()

  const bodyGrad = ctx.createLinearGradient(0, cy, 0, cy + CRANE_H)
  bodyGrad.addColorStop(0, '#e8b347')
  bodyGrad.addColorStop(0.5, '#c9943a')
  bodyGrad.addColorStop(1, '#8a6422')
  ctx.fillStyle = bodyGrad
  ctx.fillRect(cx - CRANE_W / 2, cy, CRANE_W, CRANE_H)
  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(cx - CRANE_W / 2, cy + CRANE_H - 4, CRANE_W, 2)
  ctx.fillStyle = '#1a2236'
  ctx.fillRect(cx + CRANE_W / 2 - 14, cy + 4, 12, CRANE_H - 12)
  ctx.fillStyle = '#3a5a8a'
  ctx.fillRect(cx + CRANE_W / 2 - 12, cy + 6, 8, 6)
  ctx.fillStyle = 'rgba(255,255,255,0.3)'
  ctx.fillRect(cx + CRANE_W / 2 - 12, cy + 6, 3, 1)
  ctx.fillStyle = '#3a3a3a'
  ctx.fillRect(cx - 6, cy + 4, 12, 5)
  const beaconAlpha = 0.5 + Math.sin(time * 0.4) * 0.5
  ctx.fillStyle = `rgba(220, 60, 60, ${beaconAlpha})`
  ctx.fillRect(cx - 1, cy + 1, 2, 2)
  ctx.strokeStyle = '#5a4520'
  ctx.lineWidth = 1.2
  ctx.strokeRect(cx - CRANE_W / 2, cy, CRANE_W, CRANE_H)

  const spreaderY = cy + CRANE_H + CABLE_LENGTH
  ctx.strokeStyle = '#7a7a7a'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(cx - SPREADER_W / 2 + 2, cy + CRANE_H)
  ctx.lineTo(cx - SPREADER_W / 2 + 2, spreaderY)
  ctx.moveTo(cx + SPREADER_W / 2 - 2, cy + CRANE_H)
  ctx.lineTo(cx + SPREADER_W / 2 - 2, spreaderY)
  ctx.stroke()

  ctx.fillStyle = '#c9a648'
  ctx.fillRect(cx - SPREADER_W / 2, spreaderY, SPREADER_W, SPREADER_H)
  ctx.fillStyle = 'rgba(0,0,0,0.6)'
  for (let i = 0; i < SPREADER_W; i += 4) {
    ctx.beginPath()
    ctx.moveTo(cx - SPREADER_W / 2 + i, spreaderY)
    ctx.lineTo(cx - SPREADER_W / 2 + i + 2, spreaderY)
    ctx.lineTo(cx - SPREADER_W / 2 + i, spreaderY + SPREADER_H)
    ctx.lineTo(cx - SPREADER_W / 2 + i - 2, spreaderY + SPREADER_H)
    ctx.closePath()
    ctx.fill()
  }
  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(cx - SPREADER_W / 2 + 1, spreaderY + SPREADER_H, 2, 3)
  ctx.fillRect(cx + SPREADER_W / 2 - 3, spreaderY + SPREADER_H, 2, 3)

  if (c.shieldFrames > 0) {
    ctx.strokeStyle = `rgba(58, 155, 214, ${0.5 + Math.sin(time * 0.3) * 0.3})`
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.ellipse(cx, cy + CRANE_H / 2, CRANE_W * 0.7, CRANE_H * 1.4, 0, 0, Math.PI * 2)
    ctx.stroke()
  }
}

function drawTwistlock(ctx: CanvasRenderingContext2D, t: Twistlock) {
  ctx.save()
  ctx.translate(t.x, t.y)
  ctx.rotate(t.rotation)
  ctx.fillStyle = '#ffd166'
  ctx.fillRect(-3, -3, 6, 6)
  ctx.strokeStyle = '#1a1a1a'
  ctx.lineWidth = 0.8
  ctx.strokeRect(-3, -3, 6, 6)
  ctx.restore()
  ctx.fillStyle = 'rgba(255, 209, 102, 0.35)'
  ctx.fillRect(t.x - 1, t.y - 12, 2, 10)
}

// All three enemy carriers are drawn as TOP-DOWN silhouettes with their bow /
// front pointing UP toward the terminal — the direction they're advancing.
function drawVessel(ctx: CanvasRenderingContext2D, e: Enemy) {
  const bobY = e.type === 'vessel' ? Math.sin(e.bobPhase) * 1 : 0
  const cx = e.x
  const cy = e.y + bobY

  if (e.type === 'truck') drawTopDownTruck(ctx, cx, cy, e)
  else if (e.type === 'train') drawTopDownTrain(ctx, cx, cy, e)
  else drawTopDownVessel(ctx, cx, cy, e)
}

// Top-down truck: small cab + container trailer + side wheels.
function drawTopDownTruck(ctx: CanvasRenderingContext2D, cx: number, cy: number, e: Enemy) {
  const tw = 14
  const tlen = 22
  const top = cy - tlen / 2
  const bottom = cy + tlen / 2
  const left = cx - tw / 2
  const right = cx + tw / 2

  // Drop shadow
  ctx.fillStyle = 'rgba(0,0,0,0.3)'
  ctx.fillRect(left + 1, top + 1, tw, tlen)

  // Wheels along both sides — 1 front axle + tandem rear axles
  ctx.fillStyle = '#0d0d0d'
  const wheelJitter = e.animFrame === 0 ? 0 : 1
  ctx.fillRect(left - 1, top + 5 + wheelJitter, 2, 3)
  ctx.fillRect(right - 1, top + 5 + wheelJitter, 2, 3)
  ctx.fillRect(left - 1, bottom - 9 + wheelJitter, 2, 3)
  ctx.fillRect(right - 1, bottom - 9 + wheelJitter, 2, 3)
  ctx.fillRect(left - 1, bottom - 5 + wheelJitter, 2, 3)
  ctx.fillRect(right - 1, bottom - 5 + wheelJitter, 2, 3)

  // Cab at the front (top of the screen)
  ctx.fillStyle = '#1a2236'
  ctx.fillRect(left + 1, top, tw - 2, 5)
  // Windshield glass
  const glass = ctx.createLinearGradient(0, top, 0, top + 4)
  glass.addColorStop(0, '#3a5a8a')
  glass.addColorStop(1, '#1a2540')
  ctx.fillStyle = glass
  ctx.fillRect(left + 2, top + 1, tw - 4, 3)
  // Headlights
  ctx.fillStyle = '#e8d8a8'
  ctx.fillRect(left + 1, top, 2, 1)
  ctx.fillRect(right - 3, top, 2, 1)

  // Trailer / container body
  const cTop = top + 6
  const cBot = bottom - 1
  const ch = cBot - cTop
  ctx.fillStyle = '#3a8fd6'
  ctx.fillRect(left, cTop, tw, ch)
  ctx.fillStyle = 'rgba(255,255,255,0.18)'
  ctx.fillRect(left, cTop, tw, 1)
  ctx.fillStyle = 'rgba(0,0,0,0.35)'
  ctx.fillRect(left, cBot - 1, tw, 1)
  // Roof corrugation (cross-stripes when seen from above)
  ctx.strokeStyle = 'rgba(0,0,0,0.4)'
  ctx.lineWidth = 0.5
  for (let i = 1; i < 5; i++) {
    const ly = cTop + (ch / 5) * i
    ctx.beginPath()
    ctx.moveTo(left + 1, ly)
    ctx.lineTo(right - 1, ly)
    ctx.stroke()
  }
  ctx.strokeStyle = 'rgba(0,0,0,0.5)'
  ctx.strokeRect(left, cTop, tw, ch)
  // Twistlock corner studs
  ctx.fillStyle = '#5a5a5a'
  ctx.fillRect(left, cTop, 2, 2)
  ctx.fillRect(right - 2, cTop, 2, 2)
  ctx.fillRect(left, cBot - 2, 2, 2)
  ctx.fillRect(right - 2, cBot - 2, 2, 2)

  // Exhaust puff trailing behind (below)
  if (Math.random() > 0.6) {
    ctx.fillStyle = 'rgba(140, 140, 160, 0.4)'
    ctx.beginPath()
    ctx.arc(cx + (Math.random() - 0.5) * 4, bottom + 2, 1.5, 0, Math.PI * 2)
    ctx.fill()
  }
}

// Top-down train flatcar: bogey trucks at each end + a long container on top.
// Top-down diesel locomotive: cowcatcher + cab w/ windshield up front,
// long engine hood with louvers and exhaust stack behind, bogey wheel stubs
// poking out the sides, rear coupler at the back.
function drawTopDownTrain(ctx: CanvasRenderingContext2D, cx: number, cy: number, e: Enemy) {
  const lw = 16
  const lLen = 24
  const top = cy - lLen / 2
  const bottom = cy + lLen / 2
  const left = cx - lw / 2
  const right = cx + lw / 2

  // Track ties below — flicker with animFrame to suggest motion
  if (e.animFrame === 0) {
    ctx.fillStyle = 'rgba(120, 130, 150, 0.4)'
    ctx.fillRect(cx - 6, bottom + 4, 1, 2)
    ctx.fillRect(cx + 5, bottom + 4, 1, 2)
  }

  // Drop shadow under the locomotive
  ctx.fillStyle = 'rgba(0,0,0,0.3)'
  ctx.fillRect(left + 1, top + 1, lw, lLen)

  // Front pilot / cowcatcher — sloped wedge poking out ahead of the body
  ctx.fillStyle = '#1a1a1a'
  ctx.beginPath()
  ctx.moveTo(cx, top - 2)
  ctx.lineTo(cx + 5, top + 2)
  ctx.lineTo(cx - 5, top + 2)
  ctx.closePath()
  ctx.fill()

  // Locomotive body (orange livery)
  const bodyTop = top + 1
  const bodyBot = bottom - 1
  const bodyH = bodyBot - bodyTop
  ctx.fillStyle = '#d97a2c'
  ctx.fillRect(left, bodyTop, lw, bodyH)
  // Top-of-body highlight
  ctx.fillStyle = 'rgba(255,255,255,0.18)'
  ctx.fillRect(left + 1, bodyTop, lw - 2, 1)
  // Side walkways — darker thin bands along each edge
  ctx.fillStyle = '#5a4520'
  ctx.fillRect(left, bodyTop, 1, bodyH)
  ctx.fillRect(right - 1, bodyTop, 1, bodyH)

  // Bogey wheel stubs — drawn after the body so only the parts protruding
  // past the walkways are visible.
  ctx.fillStyle = '#0d0d0d'
  ctx.fillRect(left - 2, top + 3, 2, 4)
  ctx.fillRect(right, top + 3, 2, 4)
  ctx.fillRect(left - 2, bottom - 7, 2, 4)
  ctx.fillRect(right, bottom - 7, 2, 4)
  // Wheel-rim highlights
  ctx.fillStyle = '#5a5a5a'
  ;[top + 3, top + 6, bottom - 7, bottom - 4].forEach(wy => {
    ctx.fillRect(left - 2, wy, 1, 1)
    ctx.fillRect(right + 1, wy, 1, 1)
  })

  // CAB at the front — engineer leads the way, just behind the cowcatcher
  const cabTop = bodyTop + 1
  const cabBot = bodyTop + 7
  const cabH = cabBot - cabTop
  // Cab shell (slightly darker shade than the hood for contrast)
  ctx.fillStyle = '#a85e22'
  ctx.fillRect(left + 1, cabTop, lw - 2, cabH)
  // Forward windshield — a band of glass at the very front of the cab
  const glass = ctx.createLinearGradient(0, cabTop, 0, cabTop + 3)
  glass.addColorStop(0, '#5278a8')
  glass.addColorStop(1, '#1a2540')
  ctx.fillStyle = glass
  ctx.fillRect(left + 2, cabTop, lw - 4, 3)
  // Glass shine
  ctx.fillStyle = 'rgba(255,255,255,0.35)'
  ctx.fillRect(left + 2, cabTop, lw - 6, 1)
  // Side cab windows
  ctx.fillStyle = '#1a2540'
  ctx.fillRect(left + 1, cabTop + 3, 1, 2)
  ctx.fillRect(right - 2, cabTop + 3, 1, 2)
  // Headlights flanking the windshield
  ctx.fillStyle = '#e8d8a8'
  ctx.fillRect(left + 2, cabTop, 1, 1)
  ctx.fillRect(right - 3, cabTop, 1, 1)
  // Cab outline
  ctx.strokeStyle = 'rgba(0,0,0,0.6)'
  ctx.lineWidth = 0.7
  ctx.strokeRect(left + 1, cabTop, lw - 2, cabH)

  // Long engine hood behind the cab — louvers (radiator vent stripes)
  const hoodTop = cabBot
  const hoodBot = bodyBot - 1
  const hoodH = hoodBot - hoodTop
  ctx.strokeStyle = 'rgba(0,0,0,0.4)'
  ctx.lineWidth = 0.5
  for (let i = 1; i < 6; i++) {
    const ly = hoodTop + (hoodH / 6) * i
    ctx.beginPath()
    ctx.moveTo(left + 2, ly)
    ctx.lineTo(right - 2, ly)
    ctx.stroke()
  }

  // Exhaust stack on top of the hood
  ctx.fillStyle = '#1a1a1a'
  ctx.fillRect(cx - 1, hoodTop + 1, 2, 2)
  // Smoke puff from the stack
  if (e.animFrame === 0 && Math.random() > 0.5) {
    ctx.fillStyle = 'rgba(120, 120, 130, 0.5)'
    ctx.beginPath()
    ctx.arc(cx + (Math.random() - 0.5) * 2, hoodTop, 1.5, 0, Math.PI * 2)
    ctx.fill()
  }

  // Radiator fan grille on the rear of the hood
  const fanY = hoodTop + hoodH * 0.65
  ctx.strokeStyle = 'rgba(0,0,0,0.5)'
  ctx.lineWidth = 0.6
  ctx.beginPath()
  ctx.arc(cx, fanY, 2, 0, Math.PI * 2)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(cx - 2, fanY)
  ctx.lineTo(cx + 2, fanY)
  ctx.moveTo(cx, fanY - 2)
  ctx.lineTo(cx, fanY + 2)
  ctx.stroke()

  // Body outline
  ctx.strokeStyle = 'rgba(0,0,0,0.55)'
  ctx.lineWidth = 1
  ctx.strokeRect(left, bodyTop, lw, bodyH)

  // Rear coupler
  ctx.fillStyle = '#5a5a5a'
  ctx.fillRect(cx - 1, bottom - 1, 2, 3)
}

// Top-down container ship: pointed bow, hull, container deck grid, bridge + funnel near stern.
function drawTopDownVessel(ctx: CanvasRenderingContext2D, cx: number, cy: number, e: Enemy) {
  const beam = 16
  const length = 28
  const top = cy - length / 2
  const bottom = cy + length / 2

  // Stern wake
  if (Math.random() > 0.5) {
    ctx.fillStyle = 'rgba(220, 230, 245, 0.35)'
    ctx.fillRect(cx - 5, bottom + 2, 2, 3)
    ctx.fillRect(cx + 3, bottom + 2, 2, 3)
  }
  // Bow wave
  ctx.strokeStyle = 'rgba(220, 230, 245, 0.45)'
  ctx.lineWidth = 0.8
  ctx.beginPath()
  ctx.arc(cx, top, 7, Math.PI * 1.15, Math.PI * 1.85)
  ctx.stroke()

  // Hull silhouette — pointed bow tapering to a flat stern
  ctx.fillStyle = '#d94a4a'
  ctx.beginPath()
  ctx.moveTo(cx, top)
  ctx.lineTo(cx + beam / 2, top + 7)
  ctx.lineTo(cx + beam / 2, bottom - 2)
  ctx.lineTo(cx + beam / 2 - 2, bottom)
  ctx.lineTo(cx - beam / 2 + 2, bottom)
  ctx.lineTo(cx - beam / 2, bottom - 2)
  ctx.lineTo(cx - beam / 2, top + 7)
  ctx.closePath()
  ctx.fill()
  ctx.strokeStyle = 'rgba(0,0,0,0.5)'
  ctx.lineWidth = 1
  ctx.stroke()

  // Container deck — small stable grid of colored blocks varying with cell position
  const deckTop = top + 9
  const deckBot = bottom - 7
  const deckLeft = cx - beam / 2 + 1
  const deckRight = cx + beam / 2 - 1
  const stackColors = ['#3a8fd6', '#3aa15a', '#e6c14a', '#d94a8f', '#d97a2c']
  const cols = 3
  const rows = 4
  const cW = (deckRight - deckLeft) / cols
  const cH = (deckBot - deckTop) / rows
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = (r * 7 + c * 3 + e.col + e.row * 2) % stackColors.length
      ctx.fillStyle = stackColors[idx]
      ctx.fillRect(deckLeft + c * cW + 0.4, deckTop + r * cH + 0.4, cW - 0.8, cH - 0.8)
    }
  }
  // Deck centerline (suggests container row gap)
  ctx.strokeStyle = 'rgba(0,0,0,0.4)'
  ctx.lineWidth = 0.5
  ctx.beginPath()
  ctx.moveTo(cx, deckTop)
  ctx.lineTo(cx, deckBot)
  ctx.stroke()

  // Bridge superstructure near the stern
  ctx.fillStyle = '#2a3142'
  ctx.fillRect(cx - 4, bottom - 6, 8, 4)
  // Bridge windows
  ctx.fillStyle = '#3a5a8a'
  ctx.fillRect(cx - 3, bottom - 5, 6, 1)
  ctx.fillStyle = 'rgba(255,255,255,0.3)'
  ctx.fillRect(cx - 3, bottom - 5, 2, 0.5)
  // Funnel
  ctx.fillStyle = '#1a2236'
  ctx.fillRect(cx - 1, bottom - 4, 2, 2)
  // Smoke puff
  if (e.animFrame === 0 && Math.random() > 0.7) {
    ctx.fillStyle = 'rgba(120, 120, 130, 0.5)'
    ctx.beginPath()
    ctx.arc(cx, bottom - 6, 1.5, 0, Math.PI * 2)
    ctx.fill()
  }

  // Anchor port at the bow
  ctx.fillStyle = 'rgba(0,0,0,0.55)'
  ctx.fillRect(cx - 0.5, top + 4, 1, 2)
}

function drawVesselBullet(ctx: CanvasRenderingContext2D, b: EnemyBullet) {
  if (b.fast) {
    ctx.fillStyle = '#ff4a4a'
    ctx.fillRect(b.x - 2, b.y - 4, 4, 10)
    ctx.fillStyle = 'rgba(255,74,74,0.5)'
    ctx.fillRect(b.x - 1, b.y + 4, 2, 8)
  } else {
    ctx.strokeStyle = '#d94a4a'
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.moveTo(b.x - 2, b.y + 8)
    ctx.lineTo(b.x + 2, b.y + 4)
    ctx.lineTo(b.x - 2, b.y)
    ctx.lineTo(b.x + 2, b.y - 4)
    ctx.stroke()
  }
}

function drawBunkers(ctx: CanvasRenderingContext2D, bunkers: Bunker[]) {
  for (const b of bunkers) {
    ctx.fillStyle = '#1a1a1a'
    ctx.fillRect(b.x - 4, b.y + BUNKER_GRID_H * BUNKER_CELL, BUNKER_GRID_W * BUNKER_CELL + 8, 2)
    for (let r = 0; r < BUNKER_GRID_H; r++) {
      for (let c = 0; c < BUNKER_GRID_W; c++) {
        if (!b.grid[r][c]) continue
        const x = b.x + c * BUNKER_CELL
        const y = b.y + r * BUNKER_CELL
        const colors = ['#3a8fd6', '#d97a2c', '#3aa15a', '#a83a72']
        const colorIdx = (Math.floor(c / 2) + r) % colors.length
        ctx.fillStyle = colors[colorIdx]
        ctx.fillRect(x, y, BUNKER_CELL, BUNKER_CELL)
        ctx.fillStyle = 'rgba(255,255,255,0.18)'
        ctx.fillRect(x, y, BUNKER_CELL, 1)
        ctx.fillStyle = 'rgba(0,0,0,0.35)'
        ctx.fillRect(x, y + BUNKER_CELL - 1, BUNKER_CELL, 1)
      }
    }
  }
}

function drawSpeedboat(ctx: CanvasRenderingContext2D, sb: Speedboat, time: number) {
  const cx = sb.x,
    cy = sb.y
  const facingRight = sb.dir > 0
  ctx.fillStyle = 'rgba(220, 230, 245, 0.6)'
  for (let i = 0; i < 4; i++) {
    const sx = cx - sb.dir * (i * 6 + 12)
    const sy = cy + Math.sin(sb.spray + i) * 2
    ctx.beginPath()
    ctx.arc(sx, sy, 2 + Math.sin(time * 0.4 + i) * 0.5, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.save()
  ctx.translate(cx, cy)
  if (!facingRight) ctx.scale(-1, 1)
  ctx.fillStyle = '#1a1a1a'
  ctx.beginPath()
  ctx.moveTo(20, 0)
  ctx.lineTo(8, -5)
  ctx.lineTo(-18, -5)
  ctx.lineTo(-22, 0)
  ctx.lineTo(-18, 5)
  ctx.lineTo(8, 5)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = '#d94a8f'
  ctx.fillRect(-18, -1, 28, 2)
  ctx.fillStyle = '#3a3a3a'
  ctx.fillRect(-2, -3, 8, 6)
  ctx.fillStyle = 'rgba(255,255,255,0.3)'
  ctx.fillRect(0, -2, 4, 1)
  ctx.fillStyle = '#5a5a5a'
  ctx.fillRect(-22, -2, 4, 4)
  ctx.restore()

  const pulse = 0.4 + Math.sin(time * 0.3) * 0.3
  ctx.fillStyle = `rgba(217, 74, 143, ${pulse + 0.4})`
  ctx.font = 'bold 9px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.fillText(`+${sb.score}`, cx, cy - 14)
}

function drawShieldDrop(ctx: CanvasRenderingContext2D, d: ShieldDrop, time: number) {
  const pulse = 0.6 + Math.sin(time * 0.3) * 0.4
  ctx.strokeStyle = `rgba(58, 155, 214, ${pulse})`
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.arc(d.x, d.y, 8, 0, Math.PI * 2)
  ctx.stroke()
  ctx.fillStyle = '#3a9bd6'
  ctx.beginPath()
  ctx.arc(d.x, d.y, 5, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = '#0d1218'
  ctx.font = 'bold 7px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('S', d.x, d.y)
  ctx.textBaseline = 'alphabetic'
}

function drawExplosion(ctx: CanvasRenderingContext2D, x: number, y: number, frames: number) {
  const t = 1 - frames / 18
  const r = 4 + t * 16
  ctx.strokeStyle = `rgba(255, 209, 102, ${1 - t})`
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(x - r * 0.7, y)
  ctx.lineTo(x + r * 0.7, y)
  ctx.moveTo(x, y - r * 0.7)
  ctx.lineTo(x, y + r * 0.7)
  ctx.stroke()
}

const styles: Record<string, CSSProperties> = {
  root: {
    position: 'fixed',
    inset: 0,
    background: '#080e18',
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    color: '#cfd8e8',
    zIndex: 9999,
    overflow: 'hidden',
  },
  canvas: { width: '100%', height: '100%', display: 'block', touchAction: 'none' },
  hud: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    padding: '14px 22px',
    display: 'flex',
    gap: 28,
    alignItems: 'center',
    fontSize: 11,
    letterSpacing: '0.18em',
    pointerEvents: 'none',
    zIndex: 2,
    background: 'linear-gradient(to bottom, rgba(8,14,24,0.85), rgba(8,14,24,0))',
  },
  hudGroup: { display: 'flex', flexDirection: 'column', gap: 2 },
  hudLabel: { color: '#5a6478', fontSize: 9 },
  hudValue: { color: '#ffd166', fontSize: 13, letterSpacing: '0.1em' },
  restart: {
    marginLeft: 'auto',
    pointerEvents: 'auto',
    background: 'transparent',
    border: '1px solid #4a3a18',
    color: '#ffd166',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  close: {
    marginLeft: 8,
    pointerEvents: 'auto',
    background: 'transparent',
    border: '1px solid #2a3548',
    color: '#7a8398',
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 10,
    letterSpacing: '0.18em',
    cursor: 'pointer',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: 'rgba(8, 14, 24, 0.7)',
    zIndex: 3,
    pointerEvents: 'none',
  },
  overlayTitle: {
    fontSize: 36,
    letterSpacing: '0.32em',
    color: '#ffd166',
  },
  overlaySub: { fontSize: 12, letterSpacing: '0.18em', color: '#7a8398' },
  overlaySubLarge: { fontSize: 13, letterSpacing: '0.22em', color: '#cfd8e8', marginBottom: 4 },
  legend: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 11,
    letterSpacing: '0.14em',
    color: '#cfd8e8',
    marginTop: 6,
  },
  legendRow: { display: 'flex', gap: 8, alignItems: 'center' },
  controls: {
    marginTop: 8,
    fontSize: 10,
    letterSpacing: '0.18em',
    color: '#7a8398',
    textAlign: 'center',
    lineHeight: 1.6,
  },
  report: {
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid #2a3548',
    padding: '16px 22px',
    fontSize: 11,
    lineHeight: 1.7,
    color: '#cfd8e8',
    margin: 0,
    maxWidth: 460,
  },
}
